import { lazy, Suspense, useEffect, useState } from "react";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, RotateCcw, Bot, GitCompare } from "lucide-react";

import { Topbar } from "@/components/Topbar";
import { PresetSelector } from "@/components/PresetSelector";
import { PresetDiffDialog } from "@/components/PresetDiffDialog";
import { AutonomousPanel } from "@/components/AutonomousPanel";
import { PhaseConfigurator } from "@/components/PhaseConfigurator";
import { RiskWarnings } from "@/components/RiskWarnings";
import { CodePreview } from "@/components/CodePreview";
import { SavedConfigsSheet } from "@/components/SavedConfigsSheet";
import { ParameterTable } from "@/components/ParameterTable";
const BacktestPanel = lazy(() => import("@/components/BacktestPanel").then((m) => ({ default: m.BacktestPanel })));
const NewsPanel = lazy(() => import("@/components/NewsPanel").then((m) => ({ default: m.NewsPanel })));
const NewsAnalystCard = lazy(() => import("@/components/NewsAnalystCard").then((m) => ({ default: m.NewsAnalystCard })));
const JarvisChat = lazy(() => import("@/components/JarvisChat").then((m) => ({ default: m.JarvisChat })));
const CouncilCard = lazy(() => import("@/components/CouncilCard").then((m) => ({ default: m.CouncilCard })));
const RiskGateCard = lazy(() => import("@/components/RiskGateCard").then((m) => ({ default: m.RiskGateCard })));
const ExecIntentsCard = lazy(() => import("@/components/ExecIntentsCard").then((m) => ({ default: m.ExecIntentsCard })));
const LabSection = lazy(() => import("@/components/LabSection").then((m) => ({ default: m.LabSection })));
const InvestSection = lazy(() => import("@/components/InvestSection"));
const FundCard = lazy(() => import("@/components/FundCard").then((m) => ({ default: m.FundCard })));
const PuppeteerCard = lazy(() => import("@/components/PuppeteerCard").then((m) => ({ default: m.PuppeteerCard })));
const MultiStrategyPanel = lazy(() => import("@/components/MultiStrategyPanel").then((m) => ({ default: m.MultiStrategyPanel })));
const OptimizePanel = lazy(() => import("@/components/OptimizePanel").then((m) => ({ default: m.OptimizePanel })));
const MultiBotPanel = lazy(() => import("@/components/MultiBotPanel").then((m) => ({ default: m.MultiBotPanel })));
const TelegramPanel = lazy(() => import("@/components/TelegramPanel").then((m) => ({ default: m.TelegramPanel })));
const SignalCopierCard = lazy(() => import("@/components/SignalCopierCard").then((m) => ({ default: m.SignalCopierCard })));
const XWatchCard = lazy(() => import("@/components/XWatchCard").then((m) => ({ default: m.XWatchCard })));
const PolymarketPanel = lazy(() => import("@/components/PolymarketPanel").then((m) => ({ default: m.PolymarketPanel })));
const HyperliquidPanel = lazy(() => import("@/components/HyperliquidPanel").then((m) => ({ default: m.HyperliquidPanel })));
const BrainCenter = lazy(() => import("@/components/hyperliquid/BrainCenter").then((m) => ({ default: m.BrainCenter })));
const AdvisorCard = lazy(() => import("@/components/AdvisorCard").then((m) => ({ default: m.AdvisorCard })));
const HypothesisRegistryCard = lazy(() => import("@/components/HypothesisRegistryCard").then((m) => ({ default: m.HypothesisRegistryCard })));
const ModeBadge = lazy(() => import("@/components/ModeBadge").then((m) => ({ default: m.ModeBadge })));
const ResearchPage = lazy(() => import("@/components/research/ResearchPage"));
import { BrainBrief } from "@/components/BrainBrief";
import { HubPulse } from "@/components/hub/HubPulse";
import { RealLedgerCard } from "@/components/RealLedgerCard";
import { LlmSpendCard } from "@/components/LlmSpendCard";
import { PowerRecycleCard } from "@/components/PowerRecycleCard";
import { PortfolioRiskCard } from "@/components/PortfolioRiskCard";
import { CapitalCard } from "@/components/CapitalCard";
import { AppSidebar } from "@/components/AppSidebar";
const GoldPanel = lazy(() => import("@/components/ctrader/GoldPanel").then((m) => ({ default: m.GoldPanel })));
import { PortfolioSummaryBar } from "@/components/PortfolioSummaryBar";
import { EnvBanner } from "@/components/EnvBanner";
import { SystemHealthStrip } from "@/components/SystemHealthStrip";
import { WarehouseBanner } from "@/components/WarehouseBanner";
import { MobileNav } from "@/components/MobileNav";
import { MobileViewSelect } from "@/components/MobileViewSelect";
import { LockScreen } from "@/components/LockScreen";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { usePortfolioSummary } from "@/hooks/usePortfolioSummary";
import { getParameters, getPresets, generateCode, getAuthToken } from "@/lib/api";

function Workspace() {
  const [meta, setMeta] = useState(null);
  const [config, setConfig] = useState(null);
  const [presets, setPresets] = useState([]);
  const [code, setCode] = useState("");
  const [warnings, setWarnings] = useState([]);
  const [genInfo, setGenInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activePreset, setActivePreset] = useState(null);
  const [configsOpen, setConfigsOpen] = useState(false);
  const [diffOpen, setDiffOpen] = useState(false);
  const [mode, setMode] = useState("auto"); // "auto" | "advanced"
  const [capital, setCapital] = useState(2000);
  const [currency, setCurrency] = useState("USD"); // "USD" | "EUR"
  const [section, setSection] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search).get("section");
      return ["hub", "ctrader", "polymarket", "hyperliquid", "invest", "cervello", "lab"].includes(p) ? p : "hub";
    } catch { return "hub"; }
  }); // "hub" | "ctrader" | "polymarket" | "hyperliquid" | "cervello" | "lab"
  const [navCollapsed, setNavCollapsed] = useState(() => {
    try { return localStorage.getItem("oracle-nav-collapsed") === "1"; } catch { return false; }
  });
  const toggleNav = () => setNavCollapsed((c) => {
    try { localStorage.setItem("oracle-nav-collapsed", c ? "0" : "1"); } catch { /* ignore */ }
    return !c;
  });
  const [ctab, setCtab] = useState(() => {
    try {
      const v = new URLSearchParams(window.location.search).get("view");
      const valid = ["goldlive", "telegram", "news"];
      return valid.includes(v) ? v : "goldlive";
    } catch { return "goldlive"; }
  });
  const [labTab, setLabTab] = useState("generatore");

  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const portfolio = usePortfolioSummary();
  const sectionCounts = portfolio?.sections || {};

  // Initial load (with retry — must never block the rest of the app)
  useEffect(() => {
    let cancelled = false;
    const load = async (attempt = 0) => {
      try {
        const [pm, pr] = await Promise.all([getParameters(), getPresets()]);
        if (cancelled) return;
        setMeta(pm);
        setPresets(pr.presets || []);
        setConfig({ ...pm.defaultConfig });
        setCapital(pm.defaultConfig.referenceCapital ?? 2000);
        setActivePreset("live_bilanciato");
      } catch {
        if (cancelled) return;
        if (attempt < 5) setTimeout(() => load(attempt + 1), 5000);
        else toast.error("Impossibile caricare i parametri del configuratore");
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Debounced code generation
  useEffect(() => {
    if (!config || !meta) return undefined;
    setLoading(true);
    const t = setTimeout(async () => {
      const clean = { ...config };
      Object.keys(clean).forEach((k) => {
        const v = clean[k];
        if (v === "" || (typeof v === "number" && Number.isNaN(v))) {
          clean[k] = meta.defaultConfig[k];
        }
      });
      try {
        const data = await generateCode(clean);
        setCode(data.code);
        setWarnings(data.warnings || []);
        setGenInfo({
          className: data.className,
          fileName: data.fileName,
          lineCount: data.lineCount,
        });
      } catch {
        toast.error("Errore nella generazione del codice");
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [config, meta]);

  const onParamChange = (key, value) => {
    setConfig((c) => ({ ...c, [key]: value }));
    setActivePreset(null);
  };

  // In simple (Live) mode, keep the reference capital aligned with the capital input
  // WITHOUT forcing autoMode (the Live strategy uses a fixed optimized profile).
  useEffect(() => {
    if (!config) return;
    if (mode === "auto") {
      setConfig((c) => {
        const cap = Number(capital);
        const next = Number.isNaN(cap) ? c.referenceCapital : cap;
        if (c.referenceCapital === next) return c;
        return { ...c, referenceCapital: next };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, capital]);

  const onSelectPreset = (p) => {
    setConfig({ ...p.config });
    setActivePreset(p.id);
    toast.success(`Preset applicato: ${p.name}`);
  };

  const onReset = () => {
    setConfig({ ...meta.defaultConfig });
    setActivePreset(null);
    toast.info("Parametri ripristinati ai valori predefiniti");
  };

  const onLoadConfig = (cfg) => {
    if (!meta) return;
    setConfig({ ...meta.defaultConfig, ...cfg });
    setActivePreset(null);
  };

  const handleCopy = async () => {
    const text = code || "";
    const fallbackCopy = () => {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.top = "0";
        ta.style.left = "0";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        const ok = document.execCommand("copy");
        ta.remove();
        return ok;
      } catch {
        return false;
      }
    };
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success("Codice copiato negli appunti");
        return;
      }
      throw new Error("clipboard-unavailable");
    } catch {
      if (fallbackCopy()) toast.success("Codice copiato negli appunti");
      else toast.error("Copia non riuscita");
    }
  };

  const handleDownload = () => {
    if (!code) return;
    const blob = new Blob([code], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = genInfo?.fileName || "GoldRecoveryBot.cs";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`File ${a.download} scaricato`);
  };

  const generatorReady = Boolean(meta && config);
  const generatorFallback = (
    <div className="flex items-center justify-center gap-3 py-16 text-muted-foreground" data-testid="generator-loading">
      <Loader2 className="h-5 w-5 animate-spin" />
      <span>Caricamento configuratore…</span>
    </div>
  );

  const generalBlock = generatorReady ? (
    <div className="rounded-xl border border-border bg-card p-4 shadow-[var(--shadow-sm)]">
      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <Label htmlFor="botName" className="text-sm font-medium flex items-center gap-2">
            <Bot className="h-4 w-4 text-[hsl(var(--primary))]" /> Nome del Bot
          </Label>
          <Input
            id="botName"
            data-testid="param-botName"
            className="mt-1.5"
            value={config.botName || ""}
            onChange={(e) => onParamChange("botName", e.target.value)}
            placeholder="XAUUSD Drawdown Recovery Bot"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Diventa il nome della classe C# generata{genInfo ? `: ${genInfo.className}` : ""}.
          </p>
        </div>
        <Button variant="outline" onClick={() => setDiffOpen(true)} data-testid="open-diff-button">
          <GitCompare className="h-4 w-4 mr-2" /> Confronta preset
        </Button>
        <Button variant="outline" onClick={onReset} data-testid="reset-defaults-button">
          <RotateCcw className="h-4 w-4 mr-2" /> Ripristina
        </Button>
      </div>
    </div>
  ) : null;

  const configureBlock = (
    <div className="space-y-4">
      {generalBlock}
      <PresetSelector presets={presets} activeId={activePreset} onSelect={onSelectPreset} />
      <PhaseConfigurator meta={meta} config={config} onChange={onParamChange} />
    </div>
  );

  const docBlock = generatorReady ? <ParameterTable parameters={meta.parameters} config={config} /> : null;

  const advancedBlock = (
    <Tabs defaultValue="configura">
      <TabsList>
        <TabsTrigger value="configura" data-testid="tab-configura">Configura</TabsTrigger>
        <TabsTrigger value="documentazione" data-testid="tab-documentazione">Tabella Parametri</TabsTrigger>
      </TabsList>
      <TabsContent value="configura" className="mt-4">{configureBlock}</TabsContent>
      <TabsContent value="documentazione" className="mt-4">{docBlock}</TabsContent>
    </Tabs>
  );

  const leftColumn = generatorReady ? (
    <div className="space-y-4">
      <Tabs value={mode} onValueChange={setMode}>
        <TabsList data-testid="mode-tabs">
          <TabsTrigger value="auto" data-testid="mode-auto">Live (Semplice)</TabsTrigger>
          <TabsTrigger value="advanced" data-testid="mode-advanced">Avanzata (Esperti)</TabsTrigger>
        </TabsList>
      </Tabs>
      {mode === "auto" ? (
        <AutonomousPanel
          presets={presets}
          activePresetId={activePreset}
          onSelectPreset={onSelectPreset}
          capital={capital}
          onCapitalChange={setCapital}
          currency={currency}
          onCurrencyChange={setCurrency}
          botName={config.botName}
          onBotNameChange={(v) => onParamChange("botName", v)}
          bonusExcluded={config.accountBonusExcluded ?? 0}
          onBonusChange={(v) => onParamChange("accountBonusExcluded", v)}
        />
      ) : (
        advancedBlock
      )}
    </div>
  ) : generatorFallback;

  const rightBlock = (
    <div className="space-y-4">
      <CodePreview
        code={code}
        fileName={genInfo?.fileName}
        lineCount={genInfo?.lineCount}
        onCopy={handleCopy}
        onDownload={handleDownload}
        loading={loading}
      />
      <RiskWarnings warnings={warnings} />
    </div>
  );

  // cTrader bot development tools — rendered inside the LAB section.
  const devToolsBlock = (
    <Tabs value={labTab} onValueChange={setLabTab} className="space-y-5">
      <MobileViewSelect value={labTab} onValueChange={setLabTab} testid="labdev-view"
        options={[
          { value: "generatore", label: "Generatore" },
          { value: "backtest", label: "Backtest" },
          { value: "multi", label: "Multi-Strumento" },
          { value: "optimize", label: "Ottimizzazione" },
          { value: "multibot", label: "Bot C# Multi" },
        ]} />
      <TabsList className="hidden md:flex">
        <TabsTrigger value="generatore" data-testid="view-generatore">Generatore</TabsTrigger>
        <TabsTrigger value="backtest" data-testid="view-backtest">Backtest</TabsTrigger>
        <TabsTrigger value="multi" data-testid="oracle-tabs-multi-strumento-trigger">Multi-Strumento</TabsTrigger>
        <TabsTrigger value="optimize" data-testid="oracle-tabs-ottimizzazione-trigger">Ottimizzazione</TabsTrigger>
        <TabsTrigger value="multibot" data-testid="oracle-tabs-multi-bot-trigger">Bot C# Multi</TabsTrigger>
      </TabsList>

      <TabsContent value="generatore">
        {isDesktop ? (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] gap-6">
            <div className="min-w-0">{leftColumn}</div>
            <div className="min-w-0 lg:sticky lg:top-[84px] lg:self-start">{rightBlock}</div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="min-w-0">{leftColumn}</div>
            <div className="min-w-0">{rightBlock}</div>
          </div>
        )}
      </TabsContent>

      <TabsContent value="backtest">
        {generatorReady ? (
          <BacktestPanel config={config} capital={capital} currency={currency} onCurrencyChange={setCurrency} />
        ) : generatorFallback}
      </TabsContent>

      <TabsContent value="multi">
        <MultiStrategyPanel />
      </TabsContent>

      <TabsContent value="optimize">
        <OptimizePanel />
      </TabsContent>

      <TabsContent value="multibot">
        <MultiBotPanel />
      </TabsContent>
    </Tabs>
  );

  return (
    <div className="min-h-screen app-shell-bg relative">
      <Topbar section={section} onOpenConfigs={() => setConfigsOpen(true)} />
      <AppSidebar section={section} onSelect={setSection} counts={sectionCounts}
        collapsed={navCollapsed} onToggle={toggleNav} />

      <main className={cn("w-full transition-[padding] duration-200", navCollapsed ? "lg:pl-16" : "lg:pl-64")}>
        <div className="mx-auto max-w-[1720px] px-4 md:px-8 xl:px-12 pt-6 pb-28 lg:pb-16 space-y-6">
        <EnvBanner />
        <SystemHealthStrip />
        <WarehouseBanner />

        <Suspense fallback={
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        }>
        {section === "hub" ? (
          <div className="space-y-8 md:space-y-12" data-testid="hub-view">
            <HubPulse />
            <RealLedgerCard />
            <FundCard />
            <PuppeteerCard />
            <PortfolioRiskCard />
            <CapitalCard />
            <BrainBrief />
            <PortfolioSummaryBar data={portfolio} />
          </div>
        ) : section === "ctrader" ? (
          <Tabs value={ctab} onValueChange={setCtab} className="space-y-5">
            <ModeBadge section="ctrader" />
            <MobileViewSelect value={ctab} onValueChange={setCtab} testid="ct-view"
              options={[
                { value: "goldlive", label: "Gold API (Live)" },
                { value: "copier", label: "Copy Segnali" },
                { value: "news", label: "News / Eventi" },
                { value: "telegram", label: "Telegram" },
              ]} />
            <TabsList className="hidden md:flex">
              <TabsTrigger value="goldlive" data-testid="view-goldlive">Gold API (Live)</TabsTrigger>
              <TabsTrigger value="copier" data-testid="view-copier">Copy Segnali</TabsTrigger>
              <TabsTrigger value="news" data-testid="view-news">News / Eventi</TabsTrigger>
              <TabsTrigger value="telegram" data-testid="oracle-tabs-telegram-trigger">Telegram</TabsTrigger>
            </TabsList>

            <TabsContent value="goldlive">
              <GoldPanel />
            </TabsContent>

            <TabsContent value="copier">
              <div className="max-w-2xl space-y-5">
                <SignalCopierCard />
                <XWatchCard />
              </div>
            </TabsContent>

            <TabsContent value="telegram">
              <TelegramPanel />
            </TabsContent>

            <TabsContent value="news">
              <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-4 2xl:gap-6 items-start">
                <NewsAnalystCard />
                <NewsPanel />
              </div>
            </TabsContent>
          </Tabs>
        ) : section === "polymarket" ? (
          <div className="space-y-3">
            <ModeBadge section="polymarket" />
            <PolymarketPanel />
          </div>
        ) : section === "lab" ? (
          <LabSection devTools={devToolsBlock} />
        ) : section === "invest" ? (
          <InvestSection />
        ) : section === "cervello" ? (
          <div className="space-y-6">
            <JarvisChat />
            <LlmSpendCard />
            <PowerRecycleCard />
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 2xl:gap-6 items-start">
              <AdvisorCard />
              <CouncilCard />
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 2xl:gap-6 items-start">
              <HypothesisRegistryCard />
              <RiskGateCard />
            </div>
            <ExecIntentsCard />
            <BrainCenter />
          </div>
        ) : (
          <div className="space-y-3">
            <ModeBadge section="hyperliquid" />
            <HyperliquidPanel />
          </div>
        )}
        </Suspense>
        </div>
      </main>

      <MobileNav section={section} onSelect={setSection} counts={sectionCounts} />

      <SavedConfigsSheet
        open={configsOpen}
        onOpenChange={setConfigsOpen}
        currentConfig={config}
        onLoad={onLoadConfig}
      />
      <PresetDiffDialog
        open={diffOpen}
        onOpenChange={setDiffOpen}
        presets={presets}
        meta={meta}
        config={config}
        activeId={activePreset}
        onApplyPreset={onSelectPreset}
        onRevertParam={onParamChange}
      />
      <Toaster position="top-right" richColors />
    </div>
  );
}

function App() {
  const [locked, setLocked] = useState(() => !getAuthToken());
  useEffect(() => {
    const h = () => setLocked(true);
    window.addEventListener("oracle-locked", h);
    return () => window.removeEventListener("oracle-locked", h);
  }, []);
  if (locked) return <LockScreen onUnlock={() => setLocked(false)} />;
  if (window.location.pathname === "/research") return <Suspense fallback={<div data-testid="research-route-loading" className="p-8">Caricamento ricerca…</div>}><ResearchPage /></Suspense>;
  return (
    <div className="App">
      <Workspace />
    </div>
  );
}

export default App;
